import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatDate, formatTL, STATUS_LABELS, PRIORITY_LABELS } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Search, ClipboardList, UserPlus, Sparkles, FileSpreadsheet, Trash2, Pencil, Download } from "lucide-react";
import { toast } from "sonner";
import { ChannelBadge, ConfirmDialog } from "../../components/common";
import ManualJobDialog from "../../components/ManualJobDialog";
import { CHANNELS, MANUAL_CHANNELS } from "../../lib/channels";

const emptyForm = {
  // Müşteri
  use_existing_customer: true,
  customer_id: "",
  customer_name: "", customer_phone: "", customer_email: "",
  customer_address: "", customer_city: "", customer_district: "",
  // Cihaz
  use_existing_device: false,
  device_id: "",
  brand_id: "", device_type: "kombi", device_model: "", device_serial: "", warranty_until: "",
  // İş
  fault_description: "", scheduled_at: "",
  assigned_technician_id: "", priority: "normal",
  source_channel: "telefon"
};

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [techs, setTechs] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [techF, setTechF] = useState("all");
  const [chF, setChF] = useState("all");
  const [prioF, setPrioF] = useState("all");
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");
  const [del, setDel] = useState(null);
  const [manual, setManual] = useState(false);
  const [xl, setXl] = useState(null);   // Excel penceresi

  const load = async () => {
    const [j, c, t, b] = await Promise.all([
      api.get("/jobs"), api.get("/customers"), api.get("/technicians"), api.get("/brands")
    ]);
    setJobs(j.data); setCustomers(c.data); setTechs(t.data); setBrands(b.data);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (form.use_existing_customer && form.customer_id) {
      api.get(`/customers/${form.customer_id}`).then((r) => setDevices(r.data.devices || []));
    } else {
      setDevices([]);
    }
  }, [form.customer_id, form.use_existing_customer]);

  const filtered = jobs
    .filter((j) => filter === "all" || j.status === filter)
    .filter((j) => techF === "all" || (techF === "none" ? !j.assigned_technician_id : j.assigned_technician_id === techF))
    .filter((j) => chF === "all" || (j.source_channel || "belirtilmemis") === chF)
    .filter((j) => prioF === "all" || j.priority === prioF)
    .filter((j) => !dFrom || (j.created_at || "").slice(0, 10) >= dFrom)
    .filter((j) => !dTo || (j.created_at || "").slice(0, 10) <= dTo)
    .filter((j) => !q || (j.customer_name + " " + j.job_number + " " + (j.fault_description || "")).toLowerCase().includes(q.toLowerCase()));

  const removeJob = async () => {
    try { await api.delete(`/jobs/${del.id}`); toast.success("Servis kaydı silindi"); setDel(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };
  const openExcel = () => setXl({
    status: "tamamlandi,faturalandi", basis: "completed",
    date_from: dFrom, date_to: dTo, technician_id: techF !== "all" && techF !== "none" ? techF : "all",
    channel: chF !== "all" ? chF : "all",
  });
  const downloadExcel = async () => {
    const params = { status: xl.status, basis: xl.basis };
    if (xl.date_from) params.date_from = xl.date_from;
    if (xl.date_to) params.date_to = xl.date_to;
    if (xl.technician_id !== "all") params.technician_id = xl.technician_id;
    if (xl.channel !== "all") params.channel = xl.channel;
    try {
      const res = await api.get("/export-xlsx/jobs", { params, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `yapilan-isler-${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      toast.success("Excel indirildi"); setXl(null);
    } catch (e) { toast.error("Excel oluşturulamadı"); }
  };

  const handleCreate = async () => {
    if (!form.fault_description) { toast.error("Arıza açıklaması zorunlu"); return; }
    if (form.use_existing_customer && !form.customer_id) { toast.error("Mevcut müşteri seçin veya yeni müşteri ekleyin"); return; }
    if (!form.use_existing_customer && (!form.customer_name || !form.customer_phone)) {
      toast.error("Yeni müşteri için isim ve telefon zorunlu"); return;
    }
    try {
      const payload = {
        customer_id: form.use_existing_customer ? form.customer_id : "",
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_email: form.customer_email,
        customer_address: form.customer_address,
        customer_city: form.customer_city,
        customer_district: form.customer_district,
        device_id: form.use_existing_device ? form.device_id : "",
        brand_id: form.use_existing_device ? "" : form.brand_id,
        device_type: form.device_type,
        device_model: form.device_model,
        device_serial: form.device_serial,
        warranty_until: form.warranty_until,
        fault_description: form.fault_description,
        scheduled_at: form.scheduled_at,
        assigned_technician_id: form.assigned_technician_id,
        priority: form.priority,
        source_channel: form.source_channel
      };
      await api.post("/jobs/quick", payload);
      toast.success("Servis kaydı oluşturuldu");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Servis İş Emirleri</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">
            Servis Kayıtları
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setManual(true)} data-testid="manual-job-button"><Pencil className="h-4 w-4 mr-2" />Manuel Kayıt</Button>
          <Button variant="outline" onClick={openExcel} data-testid="export-jobs-excel"><FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" />Excel indir</Button>
          <Button onClick={() => setOpen(true)} data-testid="new-job-button" className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" /> Yeni Servis Kaydı
          </Button>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-blue-600" /> Hızlı Servis Kaydı</DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="customer">
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="customer">1. Müşteri</TabsTrigger>
                <TabsTrigger value="device">2. Cihaz</TabsTrigger>
                <TabsTrigger value="job">3. Arıza & Atama</TabsTrigger>
              </TabsList>

              {/* Adım 1: Müşteri */}
              <TabsContent value="customer" className="space-y-3">
                <Tabs value={form.use_existing_customer ? "existing" : "new"} onValueChange={(v) => setForm({ ...form, use_existing_customer: v === "existing", customer_id: "" })}>
                  <TabsList>
                    <TabsTrigger value="existing">Mevcut Müşteri</TabsTrigger>
                    <TabsTrigger value="new"><UserPlus className="h-3.5 w-3.5 mr-1" /> Yeni Müşteri</TabsTrigger>
                  </TabsList>
                  <TabsContent value="existing">
                    <Label>Müşteri Seç *</Label>
                    <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                      <SelectTrigger data-testid="job-customer-select"><SelectValue placeholder="Müşteri seçin" /></SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.phone}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TabsContent>
                  <TabsContent value="new" className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Ad Soyad / Firma *</Label><Input data-testid="quick-cust-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
                      <div><Label>Telefon *</Label><Input data-testid="quick-cust-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
                    </div>
                    <div><Label>E-posta</Label><Input value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Şehir</Label><Input value={form.customer_city} onChange={(e) => setForm({ ...form, customer_city: e.target.value })} /></div>
                      <div><Label>İlçe</Label><Input value={form.customer_district} onChange={(e) => setForm({ ...form, customer_district: e.target.value })} /></div>
                    </div>
                    <div><Label>Adres</Label><Input value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} /></div>
                  </TabsContent>
                </Tabs>
              </TabsContent>

              {/* Adım 2: Cihaz */}
              <TabsContent value="device" className="space-y-3">
                {form.use_existing_customer && devices.length > 0 && (
                  <div className="flex items-center gap-2 mb-2">
                    <input type="checkbox" id="useex" checked={form.use_existing_device} onChange={(e) => setForm({ ...form, use_existing_device: e.target.checked })} />
                    <Label htmlFor="useex">Mevcut cihazı kullan</Label>
                  </div>
                )}
                {form.use_existing_device ? (
                  <div>
                    <Label>Müşterinin Cihazı</Label>
                    <Select value={form.device_id} onValueChange={(v) => setForm({ ...form, device_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Cihaz seçin" /></SelectTrigger>
                      <SelectContent>
                        {devices.map((d) => <SelectItem key={d.id} value={d.id}>{d.brand_name} {d.model} — {d.type}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Marka</Label>
                        <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                          <SelectTrigger data-testid="quick-brand-select"><SelectValue placeholder="Marka" /></SelectTrigger>
                          <SelectContent>{brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Tip</Label>
                        <Select value={form.device_type} onValueChange={(v) => setForm({ ...form, device_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kombi">Kombi</SelectItem>
                            <SelectItem value="klima">Klima</SelectItem>
                            <SelectItem value="diger">Diğer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Model</Label><Input value={form.device_model} onChange={(e) => setForm({ ...form, device_model: e.target.value })} /></div>
                      <div><Label>Seri No</Label><Input value={form.device_serial} onChange={(e) => setForm({ ...form, device_serial: e.target.value })} /></div>
                    </div>
                    <div><Label>Garanti Bitiş</Label><Input type="date" value={form.warranty_until} onChange={(e) => setForm({ ...form, warranty_until: e.target.value })} /></div>
                  </>
                )}
              </TabsContent>

              {/* Adım 3: Arıza & Atama */}
              <TabsContent value="job" className="space-y-3">
                <div>
                  <Label>Arıza Açıklaması *</Label>
                  <Textarea rows={3} data-testid="job-fault-input" value={form.fault_description} onChange={(e) => setForm({ ...form, fault_description: e.target.value })} placeholder="Müşteri ne anlatıyor?" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Teknisyen</Label>
                    <Select value={form.assigned_technician_id} onValueChange={(v) => setForm({ ...form, assigned_technician_id: v })}>
                      <SelectTrigger data-testid="quick-tech-select"><SelectValue placeholder="Atama yap" /></SelectTrigger>
                      <SelectContent>
                        {techs.filter(t => t.is_active).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} (%{t.commission_percent})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Öncelik</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dusuk">Düşük</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="yuksek">Yüksek</SelectItem>
                        <SelectItem value="acil">ACİL</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Müşteri hangi kanaldan geldi?</Label>
                  <Select value={form.source_channel || "telefon"} onValueChange={(v) => setForm({ ...form, source_channel: v })}>
                    <SelectTrigger data-testid="job-source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MANUAL_CHANNELS.map((c) => <SelectItem key={c} value={c}>{CHANNELS[c].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Planlanan Zaman</Label>
                  <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button onClick={handleCreate} data-testid="save-job-button" className="bg-blue-600 hover:bg-blue-700">Kaydet ve Oluştur</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border border-slate-200 shadow-none">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Kayıt no, müşteri veya arıza ara..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="jobs-search"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="jobs-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={techF} onValueChange={setTechF}>
            <SelectTrigger className="w-[170px]" data-testid="jobs-tech-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm ustalar</SelectItem>
              <SelectItem value="none">Atanmamış</SelectItem>
              {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={chF} onValueChange={setChF}>
            <SelectTrigger className="w-[160px]" data-testid="jobs-channel-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm kanallar</SelectItem>
              {Object.entries(CHANNELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={prioF} onValueChange={setPrioF}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm öncelikler</SelectItem>
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={dFrom} onChange={(e) => setDFrom(e.target.value)} className="w-40" title="Başlangıç" data-testid="jobs-date-from" />
          <Input type="date" value={dTo} onChange={(e) => setDTo(e.target.value)} className="w-40" title="Bitiş" data-testid="jobs-date-to" />
          {(q || filter !== "all" || techF !== "all" || chF !== "all" || prioF !== "all" || dFrom || dTo) && (
            <Button variant="ghost" onClick={() => { setQ(""); setFilter("all"); setTechF("all"); setChF("all"); setPrioF("all"); setDFrom(""); setDTo(""); }}>Temizle</Button>
          )}
          <span className="text-xs text-slate-500">{filtered.length} / {jobs.length} kayıt</span>
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Kayıt</th>
                <th className="px-4 py-3 font-semibold">Müşteri</th>
                <th className="px-4 py-3 font-semibold">Marka / Cihaz</th>
                <th className="px-4 py-3 font-semibold">Teknisyen</th>
                <th className="px-4 py-3 font-semibold">Öncelik</th>
                <th className="px-4 py-3 font-semibold">Durum</th>
                <th className="px-4 py-3 font-semibold text-right">Tutar</th>
                <th className="px-4 py-3 font-semibold">Tarih</th>
                <th className="px-4 py-3 font-semibold text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12">
                  <ClipboardList className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <div className="text-slate-500">Kayıt bulunamadı</div>
                </td></tr>
              )}
              {filtered.map((j) => (
                <tr key={j.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`job-row-${j.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/admin/jobs/${j.id}`} className="font-mono font-semibold text-blue-700 hover:underline">{j.job_number}</Link>
                    {j.source_channel && <div className="mt-1"><ChannelBadge channel={j.source_channel} /></div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{j.customer_name}</div>
                    <div className="text-xs text-slate-500">{j.customer_phone}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-700">{j.device_brand_name || "-"}</div>
                    <div className="text-xs text-slate-500">{j.device_model || j.device_type}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{j.assigned_technician_name || <span className="text-slate-400">Atanmamış</span>}</td>
                  <td className="px-4 py-3">
                    <span className={`status-pill ${PRIORITY_LABELS[j.priority]?.color}`}>{PRIORITY_LABELS[j.priority]?.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`status-pill border ${STATUS_LABELS[j.status]?.color}`}>{STATUS_LABELS[j.status]?.label}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900">{formatTL(j.total_amount)}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(j.created_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link to={`/admin/jobs/${j.id}`}><Button size="sm" variant="ghost" title="Aç / Düzenle" data-testid={`edit-job-${j.id}`}><Pencil className="h-4 w-4" /></Button></Link>
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setDel(j)} title="Sil" data-testid={`delete-job-${j.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ManualJobDialog open={manual} onOpenChange={setManual} onSaved={() => load()} />
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title={`${del?.job_number} silinsin mi?`}
        description={["tamamlandi", "faturalandi"].includes(del?.status) ? "Bu iş tamamlanmış: silinirse kasadaki gelir/usta borç kayıtları geri alınır ve kullanılan parçalar stoğa iade edilir." : "Servis kaydı kalıcı olarak silinir."}
        onConfirm={removeJob} />

      <Dialog open={!!xl} onOpenChange={(o) => !o && setXl(null)}>
        <DialogContent data-testid="excel-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-600" />Yapılan İşleri Excel'e Aktar</DialogTitle></DialogHeader>
          {xl && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Hangi işler</Label>
                  <Select value={xl.status} onValueChange={(v) => setXl({ ...xl, status: v })}>
                    <SelectTrigger data-testid="xl-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tamamlandi,faturalandi">Yapılan (tamamlanan) işler</SelectItem>
                      <SelectItem value="tamamlandi">Sadece tamamlandı</SelectItem>
                      <SelectItem value="faturalandi">Sadece faturalandı</SelectItem>
                      <SelectItem value="iptal">İptaller</SelectItem>
                      <SelectItem value="all">Tüm kayıtlar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tarih ölçütü</Label>
                  <Select value={xl.basis} onValueChange={(v) => setXl({ ...xl, basis: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="completed">Tamamlanma tarihi</SelectItem>
                      <SelectItem value="created">Oluşturma tarihi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Başlangıç</Label><Input type="date" value={xl.date_from} onChange={(e) => setXl({ ...xl, date_from: e.target.value })} data-testid="xl-from" /></div>
                <div><Label>Bitiş</Label><Input type="date" value={xl.date_to} onChange={(e) => setXl({ ...xl, date_to: e.target.value })} data-testid="xl-to" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Usta</Label>
                  <Select value={xl.technician_id} onValueChange={(v) => setXl({ ...xl, technician_id: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tüm ustalar</SelectItem>{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Kanal</Label>
                  <Select value={xl.channel} onValueChange={(v) => setXl({ ...xl, channel: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tüm kanallar</SelectItem>{Object.entries(CHANNELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-slate-500">Dosyada iş detayları, parça/işçilik/toplam, usta kalan–şirket payı, toplam satırı ve usta/kanal özet sayfası bulunur.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setXl(null)}>Vazgeç</Button>
            <Button onClick={downloadExcel} className="bg-emerald-600 hover:bg-emerald-700" data-testid="xl-download"><Download className="h-4 w-4 mr-1.5" />İndir (.xlsx)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
